import React, {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'
import {isEqual} from 'lodash'
import 'quill/dist/quill.snow.css'
import {colors} from '../../../sharedComponents/colors'
import {
  getInstructionsText,
  RichTextAttributes,
  RichTextInstructions,
  RichTextOperation,
  TASK_INSTRUCTIONS_MAX_LENGTH
} from '../../../types/richTextInstructions'
import {createQuillEditor, QuillEditorInstance} from '../../../vendor/quillAdapter'
import {Label, LabelInputWrapper} from '../../Atoms/Forms'

interface RichTextInstructionsEditorProps {
  value?: RichTextInstructions | null
  onChange: (value: RichTextInstructions | null) => void
  disabled?: boolean
  label: string
  placeholder?: string
}

const PALETTE = [
  colors.brand.cobalt,
  colors.brand.black,
  colors.system.white,
  colors.system.blue,
  colors.system.green,
  colors.system.yellow,
  colors.system.orange,
  colors.system.red,
  colors.brand.purple,
  colors.system.grey_50
]

const HEX_COLOR_PATTERN = /^#[0-9A-F]{6}$/
const MAX_CUSTOM_COLORS = 5

const normalizeOperations = (operations: RichTextOperation[]): RichTextOperation[] =>
  operations.map(operation => {
    const attributes: RichTextAttributes = {}
    if (operation.attributes?.bold) attributes.bold = true
    if (operation.attributes?.italic) attributes.italic = true
    if (operation.attributes?.underline) attributes.underline = true
    const color = operation.attributes?.color?.toUpperCase()
    if (color && HEX_COLOR_PATTERN.test(color)) attributes.color = color
    return {
      insert: operation.insert,
      ...(Object.keys(attributes).length ? {attributes} : {})
    }
  })

const RichTextInstructionsEditor = ({
  value,
  onChange,
  disabled = false,
  label,
  placeholder
}: RichTextInstructionsEditorProps) => {
  const {t} = useTranslation('tasks')
  const boundsRef = useRef<HTMLDivElement>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const quillRef = useRef<QuillEditorInstance | null>(null)
  const onChangeRef = useRef(onChange)
  const valueRef = useRef(value)
  const editorValueRef = useRef(value)
  const initialOptionsRef = useRef({disabled, label, placeholder})
  const colorControlRef = useRef<HTMLDivElement>(null)
  const customColorInputRef = useRef<HTMLInputElement>(null)
  const customColorSessionBaseRef = useRef<string[] | null>(null)
  const selectionRef = useRef<{index: number; length: number} | null>(null)
  const [colorMenuOpen, setColorMenuOpen] = useState(false)
  const [selectedColor, setSelectedColor] = useState(colors.brand.cobalt)
  const [customColors, setCustomColors] = useState<string[]>([])

  onChangeRef.current = onChange
  valueRef.current = value

  useEffect(() => {
    if (!editorRef.current || !toolbarRef.current || !boundsRef.current) return
    const preserveInlineFormatsOnEnter = (
      range: {index: number; length: number},
      context: {format: Record<string, string | boolean | undefined>}
    ) => {
      const quill = quillRef.current
      if (!quill) return true

      const formats: Array<[string, string | boolean]> = []
      if (context.format.bold) formats.push(['bold', true])
      if (context.format.italic) formats.push(['italic', true])
      if (context.format.underline) formats.push(['underline', true])
      if (typeof context.format.color === 'string') formats.push(['color', context.format.color])

      if (range.length) quill.deleteText(range.index, range.length, 'user')
      quill.insertText(range.index, '\n', 'user')
      const nextLineRange = {index: range.index + 1, length: 0}
      quill.setSelection(nextLineRange.index, nextLineRange.length, 'silent')
      formats.forEach(([format, formatValue]) => quill.format(format, formatValue, 'user'))
      quill.getModule('toolbar').update(nextLineRange)
      return false
    }

    const quill = createQuillEditor(editorRef.current, {
      bounds: boundsRef.current,
      formats: ['bold', 'italic', 'underline', 'color'],
      modules: {
        toolbar: toolbarRef.current,
        keyboard: {
          bindings: {
            preserveInlineFormatsOnEnter: {
              key: 'Enter',
              shiftKey: null,
              handler: preserveInlineFormatsOnEnter
            }
          }
        }
      },
      placeholder: initialOptionsRef.current.placeholder,
      readOnly: initialOptionsRef.current.disabled,
      theme: 'snow'
    })
    quillRef.current = quill
    quill.root.setAttribute('aria-label', initialOptionsRef.current.label)
    quill.root.setAttribute('data-cy', 'task-instructions-editor')
    if (valueRef.current) quill.setContents({ops: valueRef.current.ops}, 'silent')
    editorValueRef.current = valueRef.current

    const handleChange = () => {
      const visibleLength = quill.getLength() - 1
      if (visibleLength > TASK_INSTRUCTIONS_MAX_LENGTH) {
        quill.deleteText(TASK_INSTRUCTIONS_MAX_LENGTH, visibleLength - TASK_INSTRUCTIONS_MAX_LENGTH, 'silent')
      }
      const operations = normalizeOperations(quill.getContents().ops)
      const instructions: RichTextInstructions = {version: 1, ops: operations}
      const nextValue = getInstructionsText(instructions).length ? instructions : null
      editorValueRef.current = nextValue
      onChangeRef.current(nextValue)
    }
    const handleSelectionChange = (range: {index: number; length: number} | null) => {
      if (!range) return
      const formatColor = quill.getFormat(range).color
      setSelectedColor(
        typeof formatColor === 'string' && HEX_COLOR_PATTERN.test(formatColor.toUpperCase())
          ? formatColor.toUpperCase()
          : colors.brand.cobalt
      )
    }
    quill.on('text-change', handleChange)
    quill.on('selection-change', handleSelectionChange)
    return () => {
      quill.off('text-change', handleChange)
      quill.off('selection-change', handleSelectionChange)
      quillRef.current = null
    }
    // The editor is deliberately constructed once; subsequent mutable props are synchronized through refs/effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const quill = quillRef.current
    if (!quill || isEqual(value ?? null, editorValueRef.current ?? null)) return
    quill.setContents({ops: value?.ops || [{insert: '\n'}]}, 'silent')
    editorValueRef.current = value
  }, [value])

  useEffect(() => {
    quillRef.current?.enable(!disabled)
  }, [disabled])

  useEffect(() => {
    quillRef.current?.root.setAttribute('aria-label', label)
  }, [label])

  useEffect(() => {
    if (!colorMenuOpen) return
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!colorControlRef.current?.contains(event.target as Node)) setColorMenuOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setColorMenuOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [colorMenuOpen])

  const rememberSelection = () => {
    const selection = quillRef.current?.getSelection()
    if (selection) selectionRef.current = selection
  }

  const applyColor = (color: string | false) => {
    const quill = quillRef.current
    if (!quill) return
    if (selectionRef.current) {
      quill.setSelection(selectionRef.current.index, selectionRef.current.length, 'silent')
    }
    quill.format('color', color, 'user')
    if (color) setSelectedColor(color)
    else setSelectedColor(colors.brand.cobalt)
    setColorMenuOpen(false)
    quill.focus()
  }

  const applyCustomColor = (event: React.ChangeEvent<HTMLInputElement>) => {
    const color = event.target.value.toUpperCase()
    if (!PALETTE.some(paletteColor => paletteColor.toUpperCase() === color)) {
      const sessionBaseColors = customColorSessionBaseRef.current || customColors
      setCustomColors(
        [...sessionBaseColors.filter(currentColor => currentColor !== color), color].slice(-MAX_CUSTOM_COLORS)
      )
    }
    applyColor(color)
  }

  const count = getInstructionsText(value).length

  return (
    <LabelInputWrapper ref={boundsRef}>
      <Label>{label}</Label>
      <EditorFrame aria-disabled={disabled}>
        <Toolbar ref={toolbarRef} data-cy="task-instructions-toolbar">
          <button
            className="ql-bold"
            type="button"
            aria-label={t('tasks:format.bold', 'Bold')}
            disabled={disabled}
            onMouseDown={event => event.preventDefault()}
          />
          <button
            className="ql-italic"
            type="button"
            aria-label={t('tasks:format.italic', 'Italic')}
            disabled={disabled}
            onMouseDown={event => event.preventDefault()}
          />
          <button
            className="ql-underline"
            type="button"
            aria-label={t('tasks:format.underline', 'Underline')}
            disabled={disabled}
            onMouseDown={event => event.preventDefault()}
          />
          <ColorControl ref={colorControlRef}>
            <ColorButton
              type="button"
              aria-label={t('tasks:format.color', 'Text color')}
              aria-haspopup="menu"
              aria-expanded={colorMenuOpen}
              data-cy="task-instructions-color-button"
              disabled={disabled}
              onMouseDown={event => {
                event.preventDefault()
                rememberSelection()
              }}
              onClick={() => setColorMenuOpen(open => !open)}
            >
              <ColorLetter>A</ColorLetter>
              <ColorIndicator style={{backgroundColor: selectedColor}} />
              <ColorCaret aria-hidden="true">⌄</ColorCaret>
            </ColorButton>
            {colorMenuOpen && (
              <ColorMenu role="menu" data-cy="task-instructions-color-menu">
                <ColorSectionLabel>{t('tasks:format.defaultColors', 'Default colors')}</ColorSectionLabel>
                <PaletteGrid aria-label={t('tasks:format.basicColors', 'Basic colors')}>
                  {PALETTE.map(color => (
                    <ColorSwatch
                      key={color}
                      type="button"
                      role="menuitem"
                      aria-label={color}
                      title={color}
                      data-cy={`task-instructions-color-swatch-${color.slice(1)}`}
                      style={{backgroundColor: color}}
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => applyColor(color)}
                    />
                  ))}
                </PaletteGrid>
                <CustomColorsSection>
                  <ColorSectionLabel>{t('tasks:format.customColors', 'Custom colors')}</ColorSectionLabel>
                  <CustomColorRow aria-label={t('tasks:format.customColors', 'Custom colors')}>
                    {[...customColors].reverse().map(color => (
                      <ColorSwatch
                        key={color}
                        type="button"
                        role="menuitem"
                        aria-label={color}
                        title={color}
                        data-cy={`task-instructions-custom-color-swatch-${color.slice(1)}`}
                        style={{backgroundColor: color}}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => applyColor(color)}
                      />
                    ))}
                    {Array.from({length: MAX_CUSTOM_COLORS - customColors.length}).map((_, index) => (
                      <EmptyColorSlot key={`empty-${index}`} aria-hidden="true" />
                    ))}
                  </CustomColorRow>
                </CustomColorsSection>
                <MoreColorsControl
                  type="button"
                  role="menuitem"
                  onMouseDown={event => event.preventDefault()}
                  onClick={event => {
                    event.stopPropagation()
                    customColorSessionBaseRef.current = [...customColors]
                    customColorInputRef.current?.click()
                  }}
                >
                  <MoreColorsIcon aria-hidden="true" />
                  {t('tasks:format.moreColors', 'More colors…')}
                </MoreColorsControl>
              </ColorMenu>
            )}
          </ColorControl>
          <NativeColorInput
            ref={customColorInputRef}
            type="color"
            aria-label={t('tasks:format.moreColors', 'More colors')}
            data-cy="task-instructions-custom-color"
            value={selectedColor}
            onChange={applyCustomColor}
            tabIndex={-1}
          />
        </Toolbar>
        <Editor ref={editorRef} />
      </EditorFrame>
      <Label data-cy="task-instructions-character-count">{`${count} / ${TASK_INSTRUCTIONS_MAX_LENGTH}`}</Label>
    </LabelInputWrapper>
  )
}

const EditorFrame = styled.div`
  border: 1px solid ${colors.system.lightGrey_5};
  background: ${colors.system.lightGrey_5};

  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .ql-container.ql-snow,
  .ql-toolbar.ql-snow {
    border: 0;
  }

  .ql-editor {
    min-height: 20vmin;
    padding: 1rem 0.5rem;
    white-space: break-spaces;
  }
`

const Toolbar = styled.div`
  background: ${colors.system.white};
  border-bottom: 1px solid ${colors.system.lightGrey_5} !important;
  display: flex;
  align-items: center;
  overflow: visible;

  .ql-bold,
  .ql-italic,
  .ql-underline {
    background: transparent !important;
    border-radius: 3px;
  }

  .ql-bold.ql-active,
  .ql-italic.ql-active,
  .ql-underline.ql-active {
    background: ${colors.system.lightGrey_5} !important;
  }
`

const Editor = styled.div``

const ColorControl = styled.div`
  position: relative;
`

const ColorButton = styled.button`
  display: grid !important;
  position: relative;
  grid-template-columns: 16px;
  grid-template-rows: 15px 3px;
  align-items: center;
  justify-content: center;
  width: 28px !important;
  height: 24px !important;
  padding: 3px 5px !important;
  color: ${colors.system.grey_50};
  font-family: FavoritStd-Bold, sans-serif;
  font-size: 16px;

  &:hover,
  &[aria-expanded='true'] {
    background: ${colors.system.lightGrey_5};
  }
`

const ColorLetter = styled.span`
  grid-column: 1;
  grid-row: 1;
  line-height: 1;
`

const ColorIndicator = styled.span`
  grid-column: 1;
  grid-row: 2;
  width: 16px;
  height: 3px;
`

const ColorCaret = styled.span`
  position: absolute;
  right: 0;
  top: 8px;
  font-family: sans-serif;
  font-size: 9px;
`

const ColorMenu = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 0.35rem);
  right: 0;
  width: 192px;
  padding: 10px;
  background: ${colors.system.white};
  border: 1px solid ${colors.system.lightGrey_5};
  border-radius: 0.2rem;
  box-shadow: 0 0.35rem 1rem rgba(0, 0, 0, 0.18);

  button {
    float: none !important;
  }
`

const PaletteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 28px);
  gap: 8px;
`

const ColorSectionLabel = styled.div`
  margin-bottom: 6px;
  color: ${colors.system.grey_50};
  font-family: FavoritStd-Book, sans-serif;
  font-size: 12px;
`

const CustomColorsSection = styled.div`
  margin-top: 10px;
`

const CustomColorRow = styled.div`
  display: flex;
  gap: 8px;
`

const EmptyColorSlot = styled.span`
  box-sizing: border-box;
  width: 28px;
  height: 28px;
  border: 1px dashed ${colors.system.lightGrey_5};
  background: ${colors.system.white};
`

const ColorSwatch = styled.button`
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  border: 1px solid ${colors.system.lightGrey_5} !important;

  &:hover,
  &:focus-visible {
    outline: 2px solid ${colors.brand.cobalt};
    outline-offset: 1px;
  }
`

const MoreColorsControl = styled.button`
  display: flex !important;
  align-items: center;
  box-sizing: border-box;
  width: 100% !important;
  height: 36px !important;
  margin-top: 10px;
  padding: 6px 4px !important;
  border-top: 1px solid ${colors.system.lightGrey_5};
  color: ${colors.brand.black};
  font-family: FavoritStd-Book, sans-serif;
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${colors.system.lightGrey_5};
  }
`

const MoreColorsIcon = styled.span`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-right: 8px;
  border-radius: 50%;
  background: conic-gradient(
    ${colors.system.red},
    ${colors.system.yellow},
    ${colors.system.green},
    ${colors.system.blue},
    ${colors.brand.purple},
    ${colors.system.red}
  );
`

const NativeColorInput = styled.input`
  position: fixed;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  overflow: hidden;
  white-space: nowrap;
`

export default RichTextInstructionsEditor
